/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import SelectedProviderCard from "./SelectedProviderCard";
import SelectedServicesCard from "./SelectedServicesCard";
import SelectedDateTimeCard from "./SelectedDateTimeCard";
import Text from "../ui/Text";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/Tooltip";
import PriceBreakdown from "./PriceBreakdown";
import FormatPrice from "../formatters/FormatPrice";
import TreeView from "./tree/TreeView";
import TreeItem from "./tree/TreeItem";
import LineItem from "./LineItem";
import Flex from "../ui/Flex";

const StyledOrderDetails = styled.div`
  width: 100%;
`;

const SelectedCard = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
`;

const ProductItemPrice = styled.div`
  margin-left: auto;
`;

const TitleWrapper = styled.div`
  display: flex;
  margin-bottom: 8px;
`;

const StyledPriceBreakdown = styled(PriceBreakdown)`
  margin-top: 1rem;
  padding-inline: 1.5rem;
`;

const OrderItemWrapper = styled(Flex)`
  padding-inline: 0.5rem;
  min-width: 0;

  ${({ theme }) => theme.containerMedia.sm} {
    padding-inline: 0;
  }
`;

const OrderItemInner = styled(Flex)`
  min-width: 0;
  width: 100%;

  ${({ theme }) => theme.containerMedia.sm} {
    align-items: center;
    gap: 0.5rem;
  }
`;

const OrderItemAdditional = styled(Flex)`
  flex-shrink: 0;
  width: 2.25rem;
`;

const OrderDetails = ({
  showTimeCard,
  timeLabel,
  canEdit,
  recurring,
  services,
  provider,
  items,
  currency,
  price,
  tip,
  deposit,
  discount,
  taxes,
  invoice,
  children
}) => {
  const resolvedCurrency = currency || services?.[0]?.currency || items?.[0]?.currency;
  const hasAction = items?.some((item) => item.printLink);

  return (
    <StyledOrderDetails>
      {provider && !provider?.hide_other_providers && (
        <SelectedCard>
          <SelectedProviderCard provider={provider} canEdit={canEdit} />
        </SelectedCard>
      )}
      {showTimeCard && (
        <SelectedCard>
          <SelectedDateTimeCard canEdit={canEdit} recurringData={recurring} timeLabel={timeLabel} />
        </SelectedCard>
      )}
      {services && (
        <SelectedCard>
          <SelectedServicesCard services={services} canEdit={canEdit} />
        </SelectedCard>
      )}
      {!services && items?.length > 0 && (
        <SelectedCard>
          {invoice && (
            <TitleWrapper>
              <Text color='dark' size='large'>{`offer #`}</Text>
              <Text color='dark' size='large'>{invoice?.number}</Text>
            </TitleWrapper>
          )}
          <TreeView>
            {items.map((item) => (
              <TreeItem key={item.id}>
                <OrderItemWrapper $gap="1rem">
                  <OrderItemInner $align="center" $justify="space-between" $gap="1rem">
                    <LineItem quantity={item.qty} name={item.name} />
                    <ProductItemPrice>
                      <Text color="dark" weight="bold">
                        <FormatPrice value={item.price * item.qty} currency={resolvedCurrency} />
                      </Text>
                    </ProductItemPrice>
                  </OrderItemInner>
                  {hasAction && (
                    <OrderItemAdditional $align="center" $justify="center">
                      {item.printLink && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Button
                              variant="outline"
                              size="icon"
                              icon={<Icon.Print color="secondary" />}
                              href={item.printLink}
                              target="_blank"
                            />
                          </TooltipTrigger>
                          <TooltipContent><Text>Print gift card</Text></TooltipContent>
                        </Tooltip>
                      )}
                    </OrderItemAdditional>
                  )}
                </OrderItemWrapper>
              </TreeItem>
            ))}
          </TreeView>
        </SelectedCard>
      )}
      <StyledPriceBreakdown
        currency={resolvedCurrency}
        price={price}
        tip={tip}
        deposit={deposit}
        discount={discount}
        taxes={taxes}
      />
      {children}
    </StyledOrderDetails>
  );
};

export default OrderDetails;
